import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useRequireAuth, formatErr } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Bell, Users as UsersIcon, Send } from "lucide-react";

const ROLE_LABEL = {
  cliente: "Clienti",
  operaio: "Operai",
  personale: "Personale",
  admin: "Amministratori",
};

export default function AdminNotifications() {
  useRequireAuth("staff");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [target, setTarget] = useState("all"); // all | role | users
  const [role, setRole] = useState("cliente");
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [log, setLog] = useState([]);
  const [sending, setSending] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/admin/push/log");
      setLog(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    reload();
    api.get("/admin/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const toggleUser = (id) =>
    setSelectedUserIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const onSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Titolo e messaggio obbligatori");
      return;
    }
    if (title.length > 80 || body.length > 300) {
      toast.error("Testo troppo lungo (titolo 80, messaggio 300)");
      return;
    }
    if (target === "users" && selectedUserIds.length === 0) {
      toast.error("Seleziona almeno un utente");
      return;
    }
    setSending(true);
    try {
      const payload = { title: title.trim(), body: body.trim(), url: url || "/", target };
      if (target === "role") payload.role = role;
      if (target === "users") payload.user_ids = selectedUserIds;
      const r = await api.post("/admin/push/send", payload);
      const sent = r.data?.sent ?? 0;
      const targeted = r.data?.targeted ?? 0;
      if (sent === 0) toast.message("Nessun destinatario con notifiche attive.");
      else toast.success(`Notifica inviata a ${sent} dispositivo/i (${targeted} utenti)`);
      setTitle(""); setBody(""); setSelectedUserIds([]);
      reload();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto p-6">
        <div className="mt-6 mb-6">
          <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Amministrazione</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
            <Bell className="w-7 h-7 text-blue-800" /> Notifiche push
          </h1>
          <p className="text-slate-600 mt-2">
            Invia un avviso istantaneo agli utenti che hanno installato l'app e abilitato le notifiche.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          <Card className="p-6 md:col-span-3">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Titolo</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={80}
                  placeholder="Es. Promozione fotovoltaico"
                  data-testid="push-title"
                />
                <div className="text-xs text-slate-400 mt-1 text-right">{title.length}/80</div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Messaggio</label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={300}
                  rows={3}
                  placeholder="Es. Sconto del 10% su tutti gli impianti fino al 30/09."
                  data-testid="push-body"
                />
                <div className="text-xs text-slate-400 mt-1 text-right">{body.length}/300</div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">URL di destinazione</label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="/"
                  data-testid="push-url"
                />
                <div className="text-xs text-slate-500 mt-1">Pagina dell'app aperta quando l'utente clicca la notifica (es. <code>/</code>, <code>/dashboard</code>, <code>/lavori</code>).</div>
              </div>

              <div className="pt-2 border-t border-slate-200">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Destinatari</label>
                <Select value={target} onValueChange={(v) => setTarget(v)}>
                  <SelectTrigger data-testid="push-target"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="push-target-all">Tutti gli iscritti alle notifiche</SelectItem>
                    <SelectItem value="role" data-testid="push-target-role">Per ruolo</SelectItem>
                    <SelectItem value="users" data-testid="push-target-users">Utenti specifici</SelectItem>
                  </SelectContent>
                </Select>
                {target === "role" && (
                  <div className="mt-3">
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger data-testid="push-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cliente">Clienti</SelectItem>
                        <SelectItem value="operaio">Operai</SelectItem>
                        <SelectItem value="personale">Personale</SelectItem>
                        <SelectItem value="admin">Amministratori</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {target === "users" && (
                  <div className="mt-3 max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50">
                    {users.length === 0 && <div className="text-sm text-slate-500 p-2">Nessun utente</div>}
                    {users.map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(u.user_id)}
                          onChange={() => toggleUser(u.user_id)}
                          data-testid={`push-user-${u.user_id}`}
                        />
                        <span className="font-medium">{u.name}</span>
                        <span className="text-slate-500">— {u.email}</span>
                        <span className="ml-auto text-xs uppercase tracking-widest text-slate-400">{u.role}</span>
                      </label>
                    ))}
                    <div className="text-xs text-slate-500 mt-2 px-2">{selectedUserIds.length} selezionati</div>
                  </div>
                )}
              </div>

              <div className="pt-4 flex justify-end">
                <Button onClick={onSend} disabled={sending} className="bg-blue-800 hover:bg-blue-900" data-testid="push-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Invia notifica
                </Button>
              </div>
            </div>
          </Card>

          <Card className="p-6 md:col-span-2">
            <div className="flex items-center gap-2 mb-4 text-blue-800">
              <UsersIcon className="w-5 h-5" />
              <span className="text-xs uppercase tracking-widest font-semibold">Storico invii</span>
            </div>
            {log.length === 0 && <div className="text-sm text-slate-500">Nessun invio ancora.</div>}
            <ul className="space-y-3 max-h-[500px] overflow-y-auto pr-1" data-testid="push-log">
              {log.map((item) => (
                <li key={item.id} className="border-b border-slate-100 pb-2">
                  <div className="font-semibold text-slate-900 text-sm">{item.title}</div>
                  <div className="text-sm text-slate-600 line-clamp-2">{item.body}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {new Date(item.created_at).toLocaleString("it-IT")} · {item.sent_count}/{(item.user_ids || []).length} inviate ·
                    {" "}
                    {item.target === "all" ? "Tutti" :
                      item.target === "role" ? `Ruolo: ${ROLE_LABEL[item.role] || item.role}` :
                      `${(item.user_ids || []).length} utenti`}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
